"use client"

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

interface AdSlotProps {
  adSlot: string
  adsenseId?: string
  adFormat?: 'auto' | 'fluid' | 'rectangle'
  fullWidthResponsive?: boolean
  className?: string
  label?: string
}

export default function AdSlot({
  adSlot,
  adsenseId,
  adFormat = 'auto',
  fullWidthResponsive = true,
  className = '',
  label,
}: AdSlotProps) {
  const [isFilled, setIsFilled] = useState(false)
  const [mounted, setMounted] = useState(false)
  const adRef = useRef<HTMLModElement>(null)
  const pathname = usePathname()

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted || !adsenseId || !adSlot || pathname?.startsWith('/digital-edition')) return;

    // 1. PUSH THE AD
    try {
      // @ts-ignore
      ; (window.adsbygoogle = window.adsbygoogle || []).push({})
    } catch (err) {
      console.error('AdSense error:', err)
    }

    // 2. OBSERVE ACTUAL RENDERED HEIGHT (Bypasses Adblocker spoofing)
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // If the ad takes up space (> 10px), it's actually visible
        if (entry.contentRect.height > 10) {
          setIsFilled(true)
        } else {
          setIsFilled(false)
        }
      }
    })

    if (adRef.current) {
      resizeObserver.observe(adRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [adsenseId, adSlot, pathname, mounted])

  if (!mounted || !adsenseId || !adSlot || pathname?.startsWith('/digital-edition')) return null;

  return (
    <div className={isFilled ? className : ''}>
      {isFilled && label && (
        <div className="text-[9px] font-black uppercase tracking-[0.3em] text-muted-foreground/40 text-center mb-3">
          {label}
        </div>
      )}
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={adsenseId.startsWith('ca-') ? adsenseId : `ca-${adsenseId}`}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive.toString()}
      />
    </div>
  )
}
